import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../axios/axiosInstance';
import BrandingImageField from '../../components/BrandingImageField';

const DOMAIN_STATUS = {
    none: { label: 'Not set', bg: '#f8fafc', color: '#64748b', border: '#e2e8f0' },
    pending_dns: { label: 'Pending DNS', bg: '#fffbeb', color: '#b45309', border: '#fcd34d' },
    provisioning: { label: 'Provisioning', bg: '#eff6ff', color: '#1d4ed8', border: '#93c5fd' },
    live: { label: 'Live', bg: '#f0fdf4', color: '#16a34a', border: '#86efac' },
    failed: { label: 'Failed', bg: '#fef2f2', color: '#dc2626', border: '#fca5a5' },
    removing: { label: 'Removing', bg: '#f8fafc', color: '#64748b', border: '#cbd5e1' },
};

const ResellerBranding = () => {
    const [form, setForm] = useState({
        name: '', email: '', phone: '',
        productName: '', primaryColor: '#1B75F0', darkColor: '#051147',
        logoUrl: '', loginLogoUrl: '', faviconUrl: '',
        subdomain: '',
        customDomain: '',
        customDomainStatus: 'none',
        customDomainError: '',
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [retrying, setRetrying] = useState(false);
    const [toast, setToast] = useState(null);

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3500);
    };

    const applyFirm = useCallback((f) => {
        setForm({
            name: f.name || '',
            email: f.email || '',
            phone: f.phone || '',
            productName: f.productName || '',
            primaryColor: f.primaryColor || '#1B75F0',
            darkColor: f.darkColor || '#051147',
            logoUrl: f.logoUrl || '',
            loginLogoUrl: f.loginLogoUrl || '',
            faviconUrl: f.faviconUrl || '',
            subdomain: f.subdomain || '',
            customDomain: f.customDomain || '',
            customDomainStatus: f.customDomainStatus || 'none',
            customDomainError: f.customDomainError || '',
        });
    }, []);

    const loadFirm = useCallback(async (silent = false) => {
        try {
            const res = await axiosInstance.get('/firms/mine');
            applyFirm(res.data);
        } catch {
            if (!silent) showToast('Failed to load branding.', 'error');
        } finally {
            if (!silent) setLoading(false);
        }
    }, [applyFirm]);

    useEffect(() => {
        loadFirm(false);
    }, [loadFirm]);

    // Poll while DNS / provisioning / removing
    useEffect(() => {
        const s = form.customDomainStatus;
        if (!['pending_dns', 'provisioning', 'removing'].includes(s)) return undefined;
        const t = setInterval(() => loadFirm(true), 10000);
        return () => clearInterval(t);
    }, [form.customDomainStatus, loadFirm]);

    const setField = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const res = await axiosInstance.put('/firms/mine', {
                name: form.name || null,
                email: form.email || null,
                phone: form.phone || null,
                productName: form.productName || null,
                primaryColor: form.primaryColor,
                darkColor: form.darkColor,
                logoUrl: form.logoUrl || null,
                loginLogoUrl: form.loginLogoUrl || null,
                faviconUrl: form.faviconUrl || null,
                customDomain: (form.customDomain || '').trim(),
            });
            applyFirm(res.data);
            const branding = {
                productName: res.data.productName || form.productName || 'PayrollNoot',
                primaryColor: res.data.primaryColor || form.primaryColor,
                darkColor: res.data.darkColor || form.darkColor,
                logoUrl: res.data.logoUrl || form.logoUrl || null,
                loginLogoUrl: res.data.loginLogoUrl || form.loginLogoUrl || null,
                faviconUrl: res.data.faviconUrl || form.faviconUrl || null,
            };
            localStorage.setItem('branding', JSON.stringify(branding));
            document.documentElement.style.setProperty('--brand-primary', branding.primaryColor);
            document.documentElement.style.setProperty('--brand-dark', branding.darkColor);
            if (branding.productName) document.title = branding.productName;
            showToast('Branding saved.');
        } catch (err) {
            showToast(err?.response?.data?.message || 'Failed to save branding.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleRetry = async () => {
        setRetrying(true);
        try {
            const res = await axiosInstance.post('/firms/mine/custom-domain/retry');
            setForm(f => ({
                ...f,
                customDomainStatus: res.data.customDomainStatus || 'pending_dns',
                customDomainError: res.data.customDomainError || '',
            }));
            showToast('Retry queued — waiting for DNS / provision.');
        } catch (err) {
            showToast(err?.response?.data?.message || 'Retry failed.', 'error');
        } finally {
            setRetrying(false);
        }
    };

    const inputStyle = {
        width: '100%', padding: '9px 12px', borderRadius: 8,
        border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box',
    };

    const statusMeta = DOMAIN_STATUS[form.customDomainStatus] || DOMAIN_STATUS.none;
    const hostPreview = (form.customDomain || '').trim().toLowerCase().replace(/^https?:\/\//, '').split('/')[0];

    if (loading) {
        return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading…</div>;
    }

    return (
        <div>
            {toast && (
                <div style={{
                    position: 'fixed', top: 20, right: 24, zIndex: 9999,
                    background: toast.type === 'error' ? '#fef2f2' : '#f0fdf4',
                    border: `1px solid ${toast.type === 'error' ? '#fca5a5' : '#86efac'}`,
                    color: toast.type === 'error' ? '#dc2626' : '#16a34a',
                    borderRadius: 10, padding: '12px 20px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)', fontSize: 14, fontWeight: 500,
                }}>
                    {toast.type === 'error' ? '✗ ' : '✓ '}{toast.msg}
                </div>
            )}

            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', margin: '0 0 4px' }}>Branding</h1>
            <p style={{ color: '#64748b', margin: '0 0 24px', fontSize: 13 }}>
                Update your product name, colors, logos, and custom domain. Changes apply after save.
            </p>

            <form onSubmit={handleSave} style={{
                background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
                padding: 24, maxWidth: 560,
            }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
                    Firm Contact
                </div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Firm Name</label>
                <input style={{ ...inputStyle, marginBottom: 14 }} value={form.name} onChange={setField('name')} />
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Email</label>
                <input style={{ ...inputStyle, marginBottom: 14 }} type="email" value={form.email} onChange={setField('email')} />
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Phone</label>
                <input style={{ ...inputStyle, marginBottom: 20 }} value={form.phone} onChange={setField('phone')} />

                <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
                    White-Label
                </div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Subdomain (read-only)</label>
                {form.subdomain ? (
                    <div style={{
                        ...inputStyle, marginBottom: 14, background: '#f8fafc', color: '#334155',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                    }}>
                        <span>{form.subdomain}.payrollnoot.com</span>
                        <a
                            href={`https://${form.subdomain}.payrollnoot.com/login/company`}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: 'var(--brand-primary, #1B75F0)', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}
                        >
                            Open login →
                        </a>
                    </div>
                ) : (
                    <div style={{ ...inputStyle, marginBottom: 14, background: '#f8fafc', color: '#94a3b8' }}>
                        Not set — ask SuperAdmin to assign a subdomain
                    </div>
                )}

                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>
                    Custom Domain (optional)
                </label>
                <input
                    style={{ ...inputStyle, marginBottom: 8 }}
                    value={form.customDomain}
                    onChange={setField('customDomain')}
                    placeholder="pay.yourcompany.com"
                />
                <div style={{
                    marginBottom: 14, padding: 10, borderRadius: 8,
                    background: statusMeta.bg, border: `1px solid ${statusMeta.border}`,
                    fontSize: 12, color: statusMeta.color, lineHeight: 1.45,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                        <strong>Status: {statusMeta.label}</strong>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            {form.customDomainStatus === 'live' && hostPreview && (
                                <a
                                    href={`https://${hostPreview}/login/company`}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ color: statusMeta.color, fontWeight: 700 }}
                                >
                                    Open login →
                                </a>
                            )}
                            {form.customDomainStatus === 'failed' && (
                                <button
                                    type="button"
                                    disabled={retrying}
                                    onClick={handleRetry}
                                    style={{
                                        border: `1px solid ${statusMeta.border}`, background: '#fff',
                                        color: statusMeta.color, borderRadius: 6, padding: '4px 10px',
                                        fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                    }}
                                >
                                    {retrying ? 'Retrying…' : 'Retry'}
                                </button>
                            )}
                        </div>
                    </div>
                    <div style={{ marginTop: 6, color: '#475569' }}>
                        Prefer a subdomain CNAME → <code>payrollnoot.com</code>
                        {' '}(or A → <code>205.209.99.236</code>). After DNS points here, HTTPS is issued automatically.
                    </div>
                    {form.customDomainError ? (
                        <div style={{ marginTop: 6 }}>{form.customDomainError}</div>
                    ) : null}
                </div>

                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Product Name</label>
                <input style={{ ...inputStyle, marginBottom: 14 }} value={form.productName} onChange={setField('productName')} placeholder="Your Payroll Brand" />

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                    <div>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Primary Color</label>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input type="color" value={form.primaryColor} onChange={setField('primaryColor')}
                                style={{ width: 40, height: 36, border: '1px solid #d1d5db', borderRadius: 6, padding: 2 }} />
                            <input style={inputStyle} value={form.primaryColor} onChange={setField('primaryColor')} />
                        </div>
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Dark Color</label>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input type="color" value={form.darkColor} onChange={setField('darkColor')}
                                style={{ width: 40, height: 36, border: '1px solid #d1d5db', borderRadius: 6, padding: 2 }} />
                            <input style={inputStyle} value={form.darkColor} onChange={setField('darkColor')} />
                        </div>
                    </div>
                </div>

                <BrandingImageField
                    label="Logo"
                    kind="logo"
                    value={form.logoUrl}
                    onChange={(url) => setForm(f => ({ ...f, logoUrl: url }))}
                />
                <BrandingImageField
                    label="Login Logo"
                    kind="loginLogo"
                    value={form.loginLogoUrl}
                    onChange={(url) => setForm(f => ({ ...f, loginLogoUrl: url }))}
                />
                <BrandingImageField
                    label="Favicon / Icon"
                    kind="favicon"
                    value={form.faviconUrl}
                    onChange={(url) => setForm(f => ({ ...f, faviconUrl: url }))}
                />
                <div style={{ marginBottom: 8 }} />

                <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                    <div style={{ flex: 1, height: 36, borderRadius: 6, background: form.primaryColor, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600 }}>Primary</div>
                    <div style={{ flex: 1, height: 36, borderRadius: 6, background: form.darkColor, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600 }}>Dark</div>
                </div>

                <button type="submit" disabled={saving} style={{
                    background: 'var(--brand-primary, #1B75F0)', color: '#fff', border: 'none',
                    borderRadius: 8, padding: '10px 22px', fontWeight: 600, cursor: 'pointer', fontSize: 13,
                }}>
                    {saving ? 'Saving…' : 'Save Branding'}
                </button>
            </form>
        </div>
    );
};

export default ResellerBranding;
